/**
 * Componente: MfaChallengeStep (Paso 6.1.2)
 * Teclado / Entrada de 6 dígitos para verificación de segundo factor TOTP RFC 6238.
 */

'use client';

import React, { useState } from 'react';
import { ShieldCheck, ArrowLeft, Loader2 } from 'lucide-react';

interface MfaChallengeStepProps {
  isSubmitting: boolean;
  errorMessage?: string | null;
  onVerify: (codigoTotp: string) => Promise<unknown>;
  onBack: () => void;
}

export function MfaChallengeStep({
  isSubmitting,
  errorMessage,
  onVerify,
  onBack,
}: MfaChallengeStepProps) {
  const [codigo, setCodigo] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (codigo.trim().length !== 6) return;
    await onVerify(codigo.trim());
  };

  const handleDigitClick = (digit: string) => {
    if (codigo.length < 6) {
      setCodigo((prev) => prev + digit);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="flex items-center gap-3">
        <span className="w-11 h-11 rounded-2xl bg-emerald-100 text-[#91BE5E] flex items-center justify-center">
          <ShieldCheck size={24} />
        </span>
        <div>
          <h2 className="text-xl font-extrabold text-[#1C1938]">
            Verificación de Seguridad MFA
          </h2>
          <p className="text-xs text-gray-500">
            Ingresa el código de 6 dígitos de tu aplicación autenticadora (TOTP RFC 6238)
          </p>
        </div>
      </div>

      <div>
        <input
          type="text"
          inputMode="numeric"
          maxLength={6}
          value={codigo}
          onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000"
          className="w-full h-14 text-center text-2xl font-black tracking-[0.5em] rounded-2xl border-2 border-gray-200 focus:border-[#91BE5E] focus:outline-none text-[#1C1938]"
          aria-label="Código MFA de 6 dígitos"
        />
      </div>

      {/* Teclado rápido de apoyo */}
      <div className="grid grid-cols-3 gap-2">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'].map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => handleDigitClick(d)}
            className="h-11 rounded-xl bg-gray-50 hover:bg-gray-100 border border-gray-200 font-bold text-[#1C1938] text-base transition-colors"
          >
            {d}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setCodigo((p) => p.slice(0, -1))}
          className="col-span-2 h-11 rounded-xl bg-gray-100 hover:bg-gray-200 font-semibold text-gray-700 text-xs"
        >
          Borrar dígito
        </button>
      </div>

      {errorMessage && (
        <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-xs font-semibold text-red-700">
          {errorMessage}
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-xs font-bold text-gray-500 hover:text-gray-800"
        >
          <ArrowLeft size={15} />
          Volver
        </button>
        <button
          type="submit"
          disabled={codigo.length !== 6 || isSubmitting}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#91BE5E] hover:bg-[#78A83B] text-white font-extrabold text-sm shadow-md disabled:opacity-50"
        >
          {isSubmitting && <Loader2 size={16} className="animate-spin" />}
          <span>Verificar Acceso</span>
        </button>
      </div>
    </form>
  );
}
